import React, { useEffect, useState } from 'react';
import { Table, Input, InputNumber, DatePicker, Card, Tooltip, } from 'antd';
import { Dayjs } from 'dayjs';
import { ResourceAllocationPlanTable } from '../interfaces/interfaces';
import { ExclamationCircleOutlined, InfoCircleOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { getTooltip } from '../services/tooltip_services';
import { normalizeResourcePlan } from '../services/resourcePlan';
const { TextArea } = Input;


interface ResourceAllocationPlanProps {
  dataSource: ResourceAllocationPlanTable[];
  monthColumns: string[];
  duration: number;
  startDate: Dayjs;
  endDate: Dayjs;
  onChange: (data: ResourceAllocationPlanTable[]) => void;
  isEditable?: boolean;
  viewOnly?: boolean;
  additionalResourcingInfo: string;
  setAdditionalResourcingInfo: (value: string) => void;
  setMinFTEWarning: (value: boolean) => void;
  hoveredRowKey?: number | null;
  onRowHover?: (key: number | null) => void;
}

const monthMap: any = {
  JAN: 0,
  FEB: 1,
  MAR: 2,
  APR: 3,
  MAY: 4,
  JUN: 5,
  JUL: 6,
  AUG: 7,
  SEP: 8,
  OCT: 9,
  NOV: 10,
  DEC: 11
};


const ResourceAllocationPlan: React.FC<ResourceAllocationPlanProps> = ({ dataSource: initialData, onChange, monthColumns, duration, startDate, endDate, isEditable = true, viewOnly = false, additionalResourcingInfo, setAdditionalResourcingInfo, setMinFTEWarning, hoveredRowKey, onRowHover }) => {
  const [dataSource, setDataSource] = useState<ResourceAllocationPlanTable[]>(initialData);
  const [columns, setColumns] = useState<any[]>([]);

  useEffect(() => {
    const transformedData = initialData.map(item => normalizeResourcePlan(item, startDate, monthColumns.length));
    setDataSource(transformedData);
  }, [initialData]);

  // const fillMissingMonths = (
  //   roles: ResourceAllocationPlanTable[],
  //   expectedMonths: string[]
  // ): ResourceAllocationPlanTable[] => {
  //   return roles.map(role => {
  //     const updatedMonths = { ...role.months }; // Copy existing months

  //     // Check for missing months and add with allocation value if missing
  //     expectedMonths.forEach((month) => {
  //       updatedMonths[month] = role.allocation; // Add with allocation value
  //     });

  //     // Return updated object
  //     return { ...role, months: updatedMonths };
  //   });
  // };

  // Function to parse the month-year string and compare with start and end dates
  const isOutsideStartAndEndMonthYear = (monthYear: string, startDate: Dayjs, endDate: Dayjs) => {
    // Split the string into month and year parts
    const [month, year] = monthYear.split('-');

    // Map month abbreviations to month indices (0-based)
    const monthIndex = ["JAN", "FEB", "MAR", "APR", "MAY", "JUN", "JUL", "AUG", "SEP", "OCT", "NOV", "DEC"].indexOf(month);

    // Convert the two-digit year to a four-digit year
    const fullYear = parseInt(`20${year}`, 10);

    // Create a new dayjs date for the 1st of the given month and year
    const dateToCheck = dayjs(new Date(fullYear, monthIndex, 1));

    // Create dayjs dates for the 1st of the start and end dates' months and years
    const startMonthYear = dayjs(new Date(startDate.year(), startDate.month(), 1));
    const endMonthYear = dayjs(new Date(endDate.year(), endDate.month(), 1));

    // Check if the date is before the start date's month and year or after the end date's month and year
    return dateToCheck.isBefore(startMonthYear) || dateToCheck.isAfter(endMonthYear);
  };

  const addMonths = (date:any, n:number) => new Date(
  new Date(date).getFullYear(),
  new Date(date).getMonth() + n,
  1
);

const monthLabel = (startDate:any, offset:number) =>
  new Intl.DateTimeFormat("en-US", { month: "short", year: "numeric" })
    .format(addMonths(startDate, offset));

const getUnderMinFTEDetails = (record:any) => {
  const monthsObj = record.months || {};
  const keys = Object.keys(monthsObj).filter((k) => /^month\d+$/.test(k));
  if (keys.length === 0) return [];

  const min = (Number(record.minFTE) || 0) * 100;

  // sort month keys numerically: month0, month1, ...
  const sortedKeys = keys.sort((a, b) => Number(a.slice(5)) - Number(b.slice(5)));

  const failingLabels = sortedKeys
    .filter((k) => {
      const v = Number(monthsObj[k]);
      return Number.isFinite(v) && v < min && v !== 0;
    })
    .map((k) => monthLabel(record.startDate, Number(k.slice(5))));

  if (failingLabels.length === 0) {
    return []
  };
  if (failingLabels.length === sortedKeys.length) {
    return ["All Months"];
  }
  return failingLabels;
};


  useEffect(() => {
    // Define the expected months to be present
    const expectedMonths = monthColumns.map((month, index) => `month${index}`);

    // const updatedRoles = fillMissingMonths(dataSource, expectedMonths);
    if (dataSource.length > 0)
      onChange(dataSource);
  }, [dataSource]);

  useEffect(() => {
    // Check if any row has allocation less than minFTE
    const hasMinFTEViolation = dataSource.some(record => {
      const details = getUnderMinFTEDetails(record);
      return details.length > 0;
    });
    setMinFTEWarning(hasMinFTEViolation);
  }, [dataSource]);

  useEffect(() => {
    const generateColumns = () => {
      const cols = [
        {
          title: 'Role',
          dataIndex: 'role',
          render: (text: string, record:any) => {
            const isUnderMinFTE = getUnderMinFTEDetails(record);
            return (
            <div style={{ width: 270, display: 'flex', alignItems: 'center', gap: 8 }}>
              {/* Row number chip with a fixed-width warning slot so the numbers stay column-aligned across all rows */}
              <span
                style={{
                  position: 'relative',
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  minWidth: 28,
                  height: 28,
                  padding: '0 8px',
                  borderRadius: 6,
                  border: `1px solid ${isUnderMinFTE.length > 0 ? '#ffccc7' : '#d6e4ff'}`,
                  backgroundColor: isUnderMinFTE.length > 0 ? '#fff1f0' : '#f0f5ff',
                  color: isUnderMinFTE.length > 0 ? '#ff7b6f' : '#4096FF',
                  fontSize: 12,
                  fontWeight: 600,
                  fontVariantNumeric: 'tabular-nums',
                  flexShrink: 0,
                }}
              >
                {record.key}
                {isUnderMinFTE.length > 0 &&
                  <Tooltip title={`${isUnderMinFTE.join(', ')} Under min FTE allocation (${record.minFTE * 100} %)`}>
                    <ExclamationCircleOutlined
                      style={{
                        position: 'absolute',
                        top: -6,
                        right: -6,
                        color: "#ff7b6f",
                        fontSize: "13px",
                        background: '#fff',
                        borderRadius: '50%',
                        filter: "drop-shadow(0 0 0.4px currentColor)",
                      }}
                    />
                  </Tooltip>
                }
              </span>
              <Input value={text} disabled style={{ flex: 1, minWidth: 0 }} className="bold-input" />
            </div>
            )
          }
        },
        {
          title: (
            <span>
              Allocation %
              <Tooltip title={<span dangerouslySetInnerHTML={{ __html: getTooltip("Allocation %") || '' }} />}>
                <InfoCircleOutlined style={{ marginLeft: "10px", color: "#4096FF" }} />
              </Tooltip>
            </span>
          ),
          dataIndex: 'allocation',
          render: (text: number, record: ResourceAllocationPlanTable) => (
            <InputNumber
              value={text}
              style={{ width: 100 }}
              status={text < (record.minFTE  * 100) ? 'error' : ''}
              onChange={(value) => handleInputChange(value, record.key, 'allocation')}
              min={0}
              max={100}
              step={1}
              precision={0}
              parser={(value) => Number(value?.toString().replace(/\D/g, ""))} // Remove non-numeric characters
              disabled={viewOnly || !isEditable}
              onKeyDown={(event) => {
                if (!/^[0-9]$/.test(event.key) &&
                  event.key !== "Backspace" &&
                  event.key !== "Delete" &&
                  event.key !== "ArrowLeft" &&
                  event.key !== "ArrowRight") {
                  event.preventDefault();
                }
              }}
            />
          ),
        },
        {
          title: (
            <span>
              Start Date
              <Tooltip title={<span dangerouslySetInnerHTML={{ __html: getTooltip("Start Date") || '' }} />}>
                <InfoCircleOutlined style={{ marginLeft: "10px", color: "#4096FF" }} />
              </Tooltip>
            </span>
          ),
          dataIndex: 'startDate',
          render: (date: Dayjs, record: ResourceAllocationPlanTable) => (
            <DatePicker
              value={date > record.endDate.endOf('day') ? record.endDate.endOf('day') : date}
              allowClear={false}
              status={record.startDateWarning ? 'error' : ''}
              style={{ width: 130 }}
              onChange={(value) => handleInputChange(value, record.key, 'startDate')}
              disabledDate={(current) => {
                const startDateLimit = startDate.subtract(1, 'day').endOf('day');
                const endDateLimit = record.endDate.endOf('day');
                return current < startDateLimit || current > endDateLimit;
              }}
              disabled={viewOnly || !isEditable}
            />
          ),
        },
        {
          title: (
            <span>
              End Date
              <Tooltip title={<span dangerouslySetInnerHTML={{ __html: getTooltip("End Date") || '' }} />}>
                <InfoCircleOutlined style={{ marginLeft: "10px", color: "#4096FF" }} />
              </Tooltip>
            </span>
          ),
          dataIndex: 'endDate',
          render: (date: Dayjs, record: ResourceAllocationPlanTable) => (
            <DatePicker
              value={date}
              allowClear={false}
              status={record.endDateWarning ? 'error' : ''}
              style={{ width: 130 }}
              onChange={(value) => handleInputChange(value, record.key, 'endDate')}
              disabledDate={(current) => {
                const startDateLimit = record.startDate.endOf('day');
                const endDateLimit = endDate.endOf("month").endOf('day');

                return current < startDateLimit || current > endDateLimit;
              }}
              disabled={viewOnly || !isEditable}
            />
          ),
        },
        // {
        //   title: 'Duration (Mth)',
        //   dataIndex: 'duration',
        //   render: (value: number, record: ResourceAllocationPlanTable) => (
        //     <InputNumber
        //       value={value}
        //       parser={(value) => {
        //         const parsedValue = value ? parseInt(value, 10) : 0;
        //         return parsedValue > duration ? duration : parsedValue;
        //       }}
        //       onChange={(value) => handleInputChange(value, record.key, 'duration')}
        //       style={{ width: 120 }}
        //       min={0}
        //       step={1}
        //       disabled={!isEditable}
        //     />
        //   ),
        // },
        {
          title: (
            <span>
              Preferred Resource
              <Tooltip title={<span dangerouslySetInnerHTML={{ __html: getTooltip("Preferred Resource") || '' }} />}>
                <InfoCircleOutlined style={{ marginLeft: "10px", color: "#4096FF" }} />
              </Tooltip>
            </span>
          ),
          dataIndex: 'name',
          width: 280,
          render: (text: string, record: ResourceAllocationPlanTable) => (
            <Tooltip title={text || undefined} trigger={['hover', 'focus']}>
              <span style={{ display: 'block', width: 260 }}>
                <Input
                  aria-label="Preferred resource"
                  value={text}
                  onChange={(e) => handleInputChange(e.target.value, record.key, 'name')}
                  style={{ width: '100%' }}
                  disabled={viewOnly || !isEditable}
                />
              </span>
            </Tooltip>
          ),
        },
        {
          title: 'Notes (Skills/Exp)',
          dataIndex: 'comment',
          render: (text: string, record: ResourceAllocationPlanTable) => (
            <Input
              value={text}
              onChange={(e) => handleInputChange(e.target.value, record.key, 'comment')}
              style={{ width: 240 }}
              disabled={viewOnly || !isEditable}
            />
          ),
        },
      ];

      const dynamicMonthCols = monthColumns.map((monthTitle, i) => {
        // Parse the month and year parts
        const [month, shortYear] = monthTitle.split('-');
        // Convert the two-digit year to a four-digit year
        const fullYear = `20${shortYear}`;

        return {
          title: `${month}-${fullYear} (%)`,
          dataIndex: `month${i}`,
          render: (value: number, record: ResourceAllocationPlanTable) => {
            const isOutside = isOutsideStartAndEndMonthYear(monthTitle, record.startDate, record.endDate);
            const displayValue = isOutside ? 0 : record.months[`month${i}`] !== undefined ? record.months[`month${i}`] : record.allocation;
            return (
              <InputNumber
                value={displayValue}
                onChange={(newValue) => handleInputChange(newValue, record.key, `month${i}`)}
                style={{ width: 100 }}
                min={0}
                max={100}
                step={1}
                status={!isOutside && displayValue === 0 ? 'error' : displayValue < (record.minFTE  * 100) ? 'error' : ''}
                precision={0}
                parser={(value) => Number(value?.toString().replace(/\D/g, ""))} // Remove non-numeric characters
                disabled={viewOnly || !isEditable || isOutside}
                onKeyDown={(event) => {
                  if (!/^[0-9]$/.test(event.key) &&
                    event.key !== "Backspace" &&
                    event.key !== "Delete" &&
                    event.key !== "ArrowLeft" &&
                    event.key !== "ArrowRight") {
                    event.preventDefault();
                  }
                }}
              />
            );
          },
        };
      });

      return [...cols.slice(0, -2), ...dynamicMonthCols, ...cols.slice(-2)];
    };

    setColumns(generateColumns());
  }, [monthColumns]); // Update only when monthColumns changes

  const handleInputChange = (value: any | null, key: number, column: string) => {
    setDataSource((prevData) => {
      const newData = [...prevData];
      const index = newData.findIndex((item) => key === item.key);
      if (index > -1) {
        const item = newData[index];
        if (column.startsWith('month')) {
          item.months[column] = value || 0;
        } else if (column === 'allocation') {
          newData.splice(index, 1, { ...item, [column]: value || 0 });
          monthColumns.forEach((monthTitle, i) => {
            if (isOutsideStartAndEndMonthYear(monthTitle, item.startDate, item.endDate)) {
              item.months[`month${i}`] = 0;
            } else {
              item.months[`month${i}`] = value;
            }
          });
        } else if (column === 'startDate') {
          item.startDateWarning = false;
          newData.splice(index, 1, { ...item, [column]: value || 0 });
          monthColumns.forEach((monthTitle, i) => {
            if (isOutsideStartAndEndMonthYear(monthTitle, value, item.endDate)) {
              item.months[`month${i}`] = 0;
            } else {
              if (item.months[`month${i}`] === 0) {
                item.months[`month${i}`] = item.allocation;
              }
            }
          });
        } else if (column === 'endDate') {
          item.endDateWarning = false;
          newData.splice(index, 1, { ...item, [column]: value || 0 });
          monthColumns.forEach((monthTitle, i) => {
            if (isOutsideStartAndEndMonthYear(monthTitle, item.startDate, value)) {
              item.months[`month${i}`] = 0;
            } else {
              if (item.months[`month${i}`] === 0) {
                item.months[`month${i}`] = item.allocation;
              }
            }
          });
        } else {
          if (column === 'name' || column === 'comment') {
            newData.splice(index, 1, { ...item, [column]: value || '' });
          } else {
            newData.splice(index, 1, { ...item, [column]: value || 0 });
          }
        }
      }
      return newData;
    });
  };

  return (

    <Card title={<>Resource Allocation Plan<Tooltip title={<span dangerouslySetInnerHTML={{ __html: getTooltip("Resource Allocation Plan") || '' }} />}><InfoCircleOutlined style={{ marginLeft: "10px", marginTop: "2px", color: "#4096FF" }} /></Tooltip></>} style={{ marginBottom: '16px', boxShadow: "#8888881c 0px 0px 7px 7px", border: "2px solid #80808066" }}>
      <div style={{ width: '100%' }}>
        <Table
          dataSource={dataSource}
          columns={columns}
          rowKey="key"
          pagination={false}
          size="small"
          scroll={{ x: '100%' }}
          style={{ marginBottom: '16px' }}
          rowClassName={(record) => {
            const classes: string[] = [];
            if (getUnderMinFTEDetails(record).length > 0) classes.push('row-under-min');
            if (record.key === hoveredRowKey) classes.push('row-highlighted');
            return classes.join(' ');
          }}
          onRow={(record) => ({
            onFocus: () => onRowHover?.(record.key),
            onBlur: (e: React.FocusEvent<HTMLElement>) => {
              if (!e.currentTarget.contains(e.relatedTarget as Node)) {
                onRowHover?.(null);
              }
            },
          })}
        />
      </div>
      <div>
        <div style={{ margin: '4px 0 4px 0' }}>Additional Resourcing Information</div>
        <TextArea
          key="add-res-info"
          autoSize={{ minRows: 2, maxRows: 6 }}
          placeholder="Comments Related to Resourcing"
          value={additionalResourcingInfo}
          onChange={(e) => setAdditionalResourcingInfo(e.target.value)}
        />
      </div>
    </Card>
  );
};

export default ResourceAllocationPlan;
